import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Result, Space, Tag } from 'antd';
import { personalWorkspaceAPI } from '../api';
import type { PersonalAccess } from '../personalWorkspaceTypes';

export const stateLabels: Record<string, string> = {
  not_provisioned: '尚未登记', pending_activation: '等待账号激活', provisioning: '空间初始化中',
  active: '已登记', suspended: '已暂停', frozen: '已冻结', archived: '已归档',
  pending: '待复核', approved: '已批准', rejected: '已驳回', executed: '已执行', cancelled: '已取消',
  disabled: '已关闭', shadow: '影子观察', pilot: '实名试点', expanded: '扩大范围', all: '全员模式',
  create: '可新建', precreate: '可预建', existing: '已有空间', keep_existing: '保留现状',
  defer: '暂缓', exclude: '排除', review: '需核验', freeze: '建议冻结（不执行）',
};
export const reasonLabels: Record<string, string> = {
  AUTHENTICATED_LOGIN: '已按当前登录账号自动开通', WORKSPACE_UNAVAILABLE: '空间已停用，文件暂不可访问',
  ELIGIBLE_INTERNAL_EMPLOYEE: '已核验的在职内部员工', ACTIVATION_REQUIRED: '企业微信账号尚未激活',
  ROLLOUT_DISABLED: '当前策略未开放试点', OUTSIDE_ROLLOUT_SCOPE: '暂未列入当前试点名单',
  IDENTITY_UNVERIFIED: '员工身份待核验', EXCLUDED_DEPARTMENT: '属于排除部门',
  EXTERNAL_PARTNER: '外部合作身份', SERVICE_ACCOUNT: '系统账号', TEMPORARY_ACCOUNT: '临时账号',
  ACCOUNT_DISABLED: '账号已停用', EMPLOYMENT_ENDED: '已离职', EMPLOYMENT_UNVERIFIED: '在职状态待核验',
  WORKSPACE_EXISTS: '已有个人空间登记', REACTIVATION_REVIEW_REQUIRED: '需要独立恢复审批',
  MEMBER_NOT_IN_SNAPSHOT: '当前目录中缺少此账号', MEMBERSHIP_UNVERIFIED: '部门归属待核验',
};
export function StateTag({ state }: { state: string }) {
  return <Tag color={['approved', 'executed', 'pilot', 'create', 'precreate'].includes(state) ? 'blue'
    : ['rejected', 'frozen', 'exclude', 'freeze'].includes(state) ? 'red' : 'default'}>{stateLabels[state] || state}</Tag>;
}
export function localTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
}
export function errorInfo(error: unknown): { title: string; detail: string } {
  const e = error as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
  const status = e?.response?.status;
  const body = e?.response?.data?.detail;
  const text = typeof body === 'string' ? body : (body && !Array.isArray(body)
    ? (body as { message?: string }).message : undefined);
  return {
    title: status === 404 ? '此入口尚未启用或记录不存在' : status === 403 ? '当前账号没有操作资格'
      : status === 409 || status === 410 ? '信息已变化，请重新核对'
      : status === 503 ? '目录、策略或存储暂未就绪' : status === 422 ? '请检查提交内容' : '暂时无法完成请求',
    detail: text || (status === 422 ? '必填字段、名单或时间格式不符合要求。' : '请稍后重试；执行结果不确定时，请先查询申请回执。'),
  };
}
export function ErrorNotice({ error, retry }: { error: unknown; retry?: () => void }) {
  const info = errorInfo(error);
  return <Alert type="error" showIcon title={info.title} description={info.detail}
    action={retry ? <Button size="small" onClick={retry}>重试</Button> : undefined} />;
}
export function usePersonalAccess() {
  const [access, setAccess] = useState<PersonalAccess>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const version = ++generation.current;
    setLoading(true); setError(undefined); setAccess(undefined);
    try { const r = await personalWorkspaceAPI.access(); if (version === generation.current) setAccess(r.data); }
    catch (e) { if (version === generation.current) setError(e); }
    finally { if (version === generation.current) setLoading(false); }
  }, []);
  useEffect(() => { const counter = generation; void reload(); return () => { counter.current++; }; }, [reload]);
  return { access, error, loading, reload };
}
export function canManage(access?: PersonalAccess) {
  return !!access && (access.can_preview || access.can_review_identity || access.can_review_directory || access.can_review_policy || access.can_operate_provisioning);
}
export function PreviewNotice({ access }: { access?: PersonalAccess }) {
  return access?.environment_label ? <Alert className="pw-preview-banner" type="warning" showIcon
    title={access.environment_label} description={<Space wrap><span>所有人员和批准材料均为合成数据。</span>
      <a href="/__personal_preview">切换演示身份</a></Space>} /> : null;
}
export function NoAccess() {
  return <Result status="403" title="未获个人空间管理权限" subTitle="管理员身份和本功能授权由服务器核对，请联系负责人员。" />;
}
/** 同一页面内未确认成功的提交保留原键；执行使用申请ID作为稳定键，刷新后也可重试。 */
export function useSubmissionKey() {
  const attempt = useRef<{ body: string; key: string } | undefined>(undefined);
  return {
    get: (body: unknown) => {
      const serialized = JSON.stringify(body);
      if (!attempt.current || attempt.current.body !== serialized) {
        const random = Array.from(window.crypto.getRandomValues(new Uint32Array(4))).map(n => n.toString(16)).join('');
        attempt.current = { body: serialized, key: 'pw-' + random };
      }
      return attempt.current.key;
    },
    reset: () => { attempt.current = undefined; },
  };
}
